/**
 * Shared types, schemas, defaults, and pure-logic helpers for the Wheel of Life.
 *
 * Safe to import from anywhere — no server-only deps. Mirrors the architectural
 * pattern of src/lib/shared/* (used by both the client widget and server functions).
 *
 * Aliases: wheel of life schema, wheel defaults, wheel mapping, shape signature
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_WHEEL_LABELS = [
  "Career",
  "Money",
  "Health",
  "Family",
  "Romance",
  "Friends",
  "Personal Growth",
  "Fun",
] as const;

export type DefaultWheelLabel = (typeof DEFAULT_WHEEL_LABELS)[number];

export const WHEEL_CATEGORY_COUNT = DEFAULT_WHEEL_LABELS.length;

// ---------------------------------------------------------------------------
// Pressure-domain mapping (must match src/lib/intake/config.ts options)
// ---------------------------------------------------------------------------

export type IntakePressureDomain =
  | "career_business"
  | "health_energy"
  | "relationships"
  | "identity_direction"
  | "money_security";

export const CATEGORY_TO_PRESSURE_DOMAIN: Record<
  DefaultWheelLabel,
  IntakePressureDomain
> = {
  Career: "career_business",
  Money: "money_security",
  Health: "health_energy",
  Family: "relationships",
  Romance: "relationships",
  Friends: "relationships",
  "Personal Growth": "identity_direction",
  Fun: "identity_direction",
};

// ---------------------------------------------------------------------------
// Zod schemas (validate at boundaries — input only, never derive types from these elsewhere)
// ---------------------------------------------------------------------------

/** Max length of a custom wheel category label. Enforced at the input, the
 * client store, and the boundary schemas so the three can never disagree — a
 * mismatch previously let over-length names silently fail to save and reset
 * the whole wheel to defaults on the next load. */
export const WHEEL_LABEL_MAX_LENGTH = 40;

/**
 * Clamp a label to WHEEL_LABEL_MAX_LENGTH UTF-16 code units (what the boundary
 * schemas count via String.length) without splitting a surrogate pair. Slicing
 * mid-emoji would leave a lone high surrogate that renders as � and can break
 * JSON serialization, so drop a trailing unpaired high surrogate when the cut
 * lands mid-pair. Empty input stays empty (charCodeAt → NaN, comparison false).
 */
export function clampWheelLabel(label: string): string {
  const sliced = label.slice(0, WHEEL_LABEL_MAX_LENGTH);
  const lastCode = sliced.charCodeAt(sliced.length - 1);
  const isHighSurrogate = lastCode >= 0xd800 && lastCode <= 0xdbff;
  return isHighSurrogate ? sliced.slice(0, -1) : sliced;
}

const ScoreSchema = z.number().int().min(1).max(10);
const LabelSchema = z.string().min(1).max(WHEEL_LABEL_MAX_LENGTH);

const Labels8 = z.array(LabelSchema).length(WHEEL_CATEGORY_COUNT);
const Scores8 = z.array(ScoreSchema).length(WHEEL_CATEGORY_COUNT);

export const WheelSessionInputSchema = z.object({
  labels: Labels8,
  scores: Scores8,
  source: z.string().max(80).optional(),
});

export type WheelSessionInput = z.infer<typeof WheelSessionInputSchema>;

/** Persisted client state shape — used by the localStorage validator. */
export const WheelPersistedStateSchema = z.object({
  labels: Labels8,
  scores: Scores8,
  touchedMask: z.array(z.boolean()).length(WHEEL_CATEGORY_COUNT),
  sessionId: z.string().nullable(),
  version: z.number(),
});

export type WheelPersistedState = z.infer<typeof WheelPersistedStateSchema>;

export const WheelSuggestionSchema = z.object({
  categoryIndex: z
    .number()
    .int()
    .min(0)
    .max(WHEEL_CATEGORY_COUNT - 1),
  cihItemId: z.string(),
  cihItemType: z.enum(["article", "framework", "challenge", "ctp", "cco"]),
  score: ScoreSchema,
});

export type WheelSuggestion = z.infer<typeof WheelSuggestionSchema>;

/** A suggestion with its resolved on-site title, optional excerpt, and href. */
export type ResolvedWheelSuggestion = WheelSuggestion & {
  title: string;
  excerpt: string | null;
  href: string;
};

// ---------------------------------------------------------------------------
// Pure-logic helpers
// ---------------------------------------------------------------------------

/**
 * Indices of weak areas: score < 5, sorted ascending by score, max 3.
 * Used to pick which categories get an Iseek suggestion card on the results page.
 */
export function selectWeakAreas(scores: number[]): number[] {
  return scores
    .map((score, index) => ({ score, index }))
    .filter((s) => s.score < 5)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .slice(0, 3)
    .map((s) => s.index);
}

// ---------------------------------------------------------------------------
// Wheel category → coaching challenge mapping
// ---------------------------------------------------------------------------

/**
 * Wheel category index → coaching challenge slug (null when no challenge fits).
 *
 * Indices match DEFAULT_WHEEL_LABELS order:
 *   0 Career, 1 Money, 2 Health, 3 Family, 4 Romance, 5 Friends,
 *   6 Personal Growth, 7 Fun
 *
 * Hard-coded against production challenge slugs: the wheel has eight fixed areas
 * and the challenge set is stable. Money (index 1) has no dedicated
 * financial-coaching challenge yet, so it maps to null and shows no card.
 * Family/Romance/Friends share the relationships challenge; Personal Growth/Fun
 * share life redesign — the planner below dedupes those so a single card shows.
 */
export const WHEEL_INDEX_TO_CHALLENGE_SLUG: Record<number, string | null> = {
  0: "career-crossroads",
  1: null,
  2: "burnout-overwhelm",
  3: "relationships-at-a-breaking-point",
  4: "relationships-at-a-breaking-point",
  5: "relationships-at-a-breaking-point",
  6: "life-redesign-purpose",
  7: "life-redesign-purpose",
};

export type WheelSuggestionPlan = {
  categoryIndex: number;
  slug: string;
  score: number;
};

/**
 * Pure planner from scores to an ordered, deduped set of challenge slugs to
 * surface as suggestion cards. Most-stretched area first; areas that map to the
 * same challenge collapse to the first (lowest-scoring) occurrence; unmapped
 * areas (e.g. Money) are dropped. The server resolver turns each slug into a
 * concrete published challenge.
 */
export function planWheelSuggestions(scores: number[]): WheelSuggestionPlan[] {
  // Dedupe by slug (first weak area wins) via a Map keyed on slug. selectWeakAreas
  // is already sorted most-stretched first, and Map preserves insertion order.
  const bySlug = selectWeakAreas(scores).reduce<
    Map<string, WheelSuggestionPlan>
  >((acc, idx) => {
    const slug = WHEEL_INDEX_TO_CHALLENGE_SLUG[idx];
    if (slug && !acc.has(slug)) {
      acc.set(slug, { categoryIndex: idx, slug, score: scores[idx] });
    }
    return acc;
  }, new Map());
  return Array.from(bySlug.values());
}

/**
 * Index-based mapping from wheel category to an intake-flow Q1 trigger value
 * (the `transitionTrigger` step in src/lib/intake/config.ts). Used to pre-seed
 * the intake flow when a user arrives from the Wheel of Life results page —
 * the trigger is a best guess that the user can correct on Q1.
 *
 * Pairs with CATEGORY_TO_PRESSURE_DOMAIN: pressureDomain answers Q2,
 * trigger answers Q1.
 */
export const CATEGORY_TO_INTAKE_TRIGGER: Record<DefaultWheelLabel, string> = {
  Career: "not_executing",
  Money: "income_impact",
  Health: "change_now",
  Family: "relationships",
  Romance: "relationships",
  Friends: "relationships",
  "Personal Growth": "redesign_life",
  Fun: "redesign_life",
};

/**
 * Pick the single best intake trigger for a wheel session — drawn from the
 * weakest area. Returns null when nothing is weak (round wheel) so the caller
 * can leave Q1 unanswered.
 */
export function inferIntakeTriggerFromScores(scores: number[]): string | null {
  const weak = selectWeakAreas(scores);
  if (weak.length === 0) return null;
  const defaultLabel = DEFAULT_WHEEL_LABELS[weak[0]];
  return CATEGORY_TO_INTAKE_TRIGGER[defaultLabel] ?? null;
}

/**
 * Derive up to 2 IntakePressureDomain values from the user's weakest score indices.
 * Index-based: each weak position maps via DEFAULT_WHEEL_LABELS[index] regardless
 * of whether the user renamed the category. Deduped, max 2 results.
 */
export function inferPressureDomainsFromScores(
  scores: number[],
): IntakePressureDomain[] {
  const weak = selectWeakAreas(scores);
  const seen = new Set<IntakePressureDomain>();
  for (const idx of weak) {
    const defaultLabel = DEFAULT_WHEEL_LABELS[idx];
    const domain = CATEGORY_TO_PRESSURE_DOMAIN[defaultLabel];
    if (domain) seen.add(domain);
    if (seen.size === 2) break;
  }
  return Array.from(seen);
}

/**
 * Derive a coarse "shape signature" used to seed the AI reflection prompt.
 * Returned values are stable strings the prompt template knows how to interpret.
 */
export type ShapeSignature =
  | "even-flat"
  | "even-high"
  | "even-low"
  | "polarized"
  | "spiky-low"
  | "spiky-high"
  | "mixed";

export function computeShapeSignature(scores: number[]): ShapeSignature {
  const avg = scores.reduce((s, n) => s + n, 0) / scores.length;
  const variance =
    scores.reduce((s, n) => s + (n - avg) ** 2, 0) / scores.length;
  const stdev = Math.sqrt(variance);

  if (stdev < 1.0) {
    if (avg >= 7) return "even-high";
    if (avg <= 4) return "even-low";
    return "even-flat";
  }

  if (stdev > 3.0) return "polarized";

  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const lowOutlierCount = scores.filter((s) => s <= min + 1).length;
  const highOutlierCount = scores.filter((s) => s >= max - 1).length;
  if (lowOutlierCount === 1 && avg >= 6) return "spiky-low";
  if (highOutlierCount === 1 && avg <= 5) return "spiky-high";
  return "mixed";
}
